import { useOpenSection } from 'components/section-edit/SectionEdit';
import { useInvoice } from 'pages/invoices/hooks/useInvoice';
import { INVOICE_TAB, InvoiceTabId } from 'pages/invoices/util/invoiceTabAlerts';
import { useCallback } from 'react';
import { InvoiceData, InvoiceMatterDetailsDto } from 'types/invoices';
import { useHasPermissions } from 'utils/permissionUtil';

/** The side column's Billing notes card, as a `SectionEdit` id. */
export const BILLING_NOTES_SECTION_ID = 'billing-notes';

/** A case's card on Cases, as a `SectionEdit` id. */
export const caseSectionId = (matterId: number) => `case-${matterId}`;

/**
 * The tab a card open in edit counts on. A case's card is on Cases; Billing notes sits beside every tab and counts on
 * General, where its other opener (the payer's "Edit payer billing notes") is; every other card is General's.
 */
export const tabOfSection = (sectionId: string): InvoiceTabId =>
    sectionId.startsWith('case-') ? INVOICE_TAB.CASES : INVOICE_TAB.GENERAL;

/**
 * Opens the Billing notes card in edit from another card. Its first field takes focus as it opens, which brings the
 * card into view.
 */
export const useOpenBillingNotesEdit = () => {
    const open = useOpenSection();
    return useCallback(() => open?.(BILLING_NOTES_SECTION_ID), [open]);
};

/** Whether the user may edit the invoice's billing notes: with the permission, and not while it is postponed. */
export const useCanEditBillingNotes = (invoice: InvoiceData) => {
    const hasPermission = useHasPermissions(['UPDATE.INVOICES.BILLING_NOTES']);
    const { isPostponed } = useInvoice(invoice);
    return hasPermission && !isPostponed;
};

/** The order of an invoice's cases on its tabs and cards: by case number. */
export const byCaseNumber = (a: InvoiceMatterDetailsDto, b: InvoiceMatterDetailsDto) =>
    (a.matterNumber ?? '').localeCompare(b.matterNumber ?? '', undefined, { numeric: true });
